import { Building2, Loader2, X } from "lucide-react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { type AdminAccountSummary, useSetAccountMcp } from "@/lib/api";

import { usePendingIdSet } from "./use-pending-id-set";

interface AccountTableProps {
  accounts: AdminAccountSummary[];
}

export function AccountTable({ accounts }: AccountTableProps) {
  const setMcp = useSetAccountMcp();
  // A single shared mutation can't attribute in-flight/failed state to the
  // right row when two rows are toggled at once, so track both per-row here.
  const pending = usePendingIdSet();
  const failed = usePendingIdSet();

  const handleToggle = (account: AdminAccountSummary, enabled: boolean) => {
    pending.add(account.account_id);
    failed.remove(account.account_id);
    setMcp.mutate(
      { accountId: account.account_id, enabled },
      {
        onError: () => failed.add(account.account_id),
        onSettled: () => pending.remove(account.account_id),
      },
    );
  };

  const failedAccounts = accounts.filter((account) => failed.ids.has(account.account_id));

  return (
    <>
      {failedAccounts.length > 0 && (
        <div className="bg-destructive/10 border-destructive/30 text-destructive mb-4 flex items-center justify-between rounded-lg border px-4 py-3 text-sm">
          <span>
            Could not update MCP access for{" "}
            {failedAccounts.map((account) => account.account_name).join(", ")}. Please try again.
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive"
            onClick={() => failed.reset()}
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <div className="bg-card border-border overflow-hidden rounded-lg border shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-warm-gray-01 border-border border-b">
              <tr>
                <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                  Account
                </th>
                <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                  ID
                </th>
                <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                  Segment
                </th>
                <th className="text-muted px-4 py-3 text-right text-[10px] font-medium tracking-[0.16em] uppercase">
                  MCP access
                </th>
              </tr>
            </thead>
            <tbody className="divide-border divide-y">
              {accounts.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <div className="flex flex-col items-center justify-center gap-1.5 px-6 py-12 text-center">
                      <Building2 className="text-muted h-6 w-6" />
                      <p className="text-foreground text-sm font-medium">No accounts yet</p>
                      <p className="text-muted max-w-xs text-xs">
                        Create a customer account to start onboarding contacts and granting MCP
                        access.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                accounts.map((account) => {
                  const isPending = pending.ids.has(account.account_id);
                  return (
                    <tr
                      key={account.account_id}
                      className="hover:bg-warm-gray-01 transition-colors"
                    >
                      <td className="px-4 py-3 align-middle font-medium">
                        <Link
                          to={`/admin/accounts/${account.account_id}`}
                          className="hover:text-accent underline-offset-2 hover:underline"
                        >
                          {account.account_name}
                        </Link>
                      </td>
                      <td className="text-muted px-4 py-3 align-middle font-mono text-xs">
                        {account.account_id}
                      </td>
                      <td className="text-muted px-4 py-3 align-middle">
                        {account.segment ?? "—"}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center justify-end gap-2">
                          {isPending && <Loader2 className="text-muted h-4 w-4 animate-spin" />}
                          <Switch
                            checked={account.mcp_enabled}
                            disabled={isPending}
                            onCheckedChange={(enabled) => handleToggle(account, enabled)}
                            aria-label={`MCP access for ${account.account_name}`}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
